import { Box, Typography } from "@mui/material";
import styled, { css, keyframes } from "styled-components";

export type ToastVariant = "success" | "error" | "warning" | "info";

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const progressShrink = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

export const ToastContainer = styled(Box)<{
  $variant: ToastVariant;
  $isExiting?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space(30)};
  min-width: 320px;
  max-width: 420px;
  padding: ${({ theme }) => theme.space(40)};
  background: rgb(21, 58, 69);
  border-radius: ${({ theme }) => theme.radius.default};
  box-shadow: ${({ theme }) => theme.shadow[30]};
  position: relative;
  overflow: hidden;
  animation: ${({ $isExiting }) =>
    $isExiting
      ? css`
          ${slideOut} 0.2s ease-in forwards
        `
      : css`
          ${slideIn} 0.3s ease-out
        `};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${({ theme, $variant }) => {
      switch ($variant) {
        case "success":
          return theme.palette.success.main;
        case "error":
          return theme.palette.error.main;
        case "warning":
          return theme.palette.warning.main;
        case "info":
          return theme.palette.info.main;
        default:
          return theme.palette.info.main;
      }
    }};
  }
`;

export const IconWrapper = styled(Box)<{ $variant: ToastVariant }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case "success":
        return `${theme.palette.success.main}14`;
      case "error":
        return `${theme.palette.error.main}14`;
      case "warning":
        return `${theme.palette.warning.main}14`;
      case "info":
        return `${theme.palette.info.main}14`;
      default:
        return `${theme.palette.info.main}14`;
    }
  }};

  svg {
    font-size: 18px;
    color: ${({ theme, $variant }) => {
      switch ($variant) {
        case "success":
          return theme.palette.success.main;
        case "error":
          return theme.palette.error.main;
        case "warning":
          return theme.palette.warning.main;
        case "info":
          return theme.palette.info.main;
        default:
          return theme.palette.info.main;
      }
    }};
  }
`;

export const ContentWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(10)};
  flex: 1;
  min-width: 0;
`;

export const Title = styled(Typography)`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const Message = styled(Typography)`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.text.secondary};
  word-wrap: break-word;
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: ${({ theme }) => theme.radius[10]};
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
    color: ${({ theme }) => theme.palette.text.primary};
  }

  svg {
    font-size: 18px;
  }
`;

export const ProgressBar = styled(Box)<{
  $variant: ToastVariant;
  $duration: number;
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case "success":
        return theme.palette.success.main;
      case "error":
        return theme.palette.error.main;
      case "warning":
        return theme.palette.warning.main;
      case "info":
        return theme.palette.info.main;
      default:
        return theme.palette.info.main;
    }
  }};
  opacity: 0.6;
  animation: ${({ $duration }) => css`
    ${progressShrink} ${$duration}ms linear forwards
  `};
`;
